import * as Sentry from '@sentry/browser';

import React, { Suspense } from 'react';
import PropTypes from 'prop-types';

import { getMenhirUrl } from '../../utils/config';

import useAppContext from '../../context/useAppContext';
import useSeatedContext, { SeatedContextProvider } from '../../context/seatedContext';
import { ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';

import { Switch, Route, Redirect } from 'react-router-dom';
import TicketDesignEventRouter from './ticketDesignEventRouter';

import WeezBoRoute from '../migrations/weezRoute';
import { default as routes } from '../migrations/routes';
import InventoryManager from '../../components/seating/inventoryManager';
import Loading from '../../components/loading';

import EventModel from '../../models/eventModel';

import Attendees from '../../pages/attendees';
import AttendeesStats from '../../pages/attendees_stats';
import AddAttendee from '../../pages/attendee/add-attendee';
import AttendeesRedirect from '../../pages/attendee/add-attendee/attendees-redirect';
import InvitationsLight from '../../pages/attendee/invitations';
import InvitationsRedirect from '../../pages/attendee/invitations/invitations-redirect';
import PrintStockRedirect from '../../pages/attendee/print-stock/print-stock-redirect';
import Import from '../../pages/attendees/import/Import';
import ImportNew from '../../pages/attendees/import/ImportNew';
import Template from '../template/template';
import Queue from '../../pages/queue';
import EventDashboard from '../../pages/events/dashboard/eventDashboard';
import Rates from '../../pages/rates/rates';
import Quotas from '../../pages/quotas';
import QuotasDev from '../../pages/quotasDev/quotas.js';
import Availability from '../../pages/zoneAvailability/availability';
import MinisiteManager from '../../pages/minisite';
import Manager from '../../pages/manager/manager';

import Forms from '../../pages/form/router';
import Subscriptions from '../../pages/subscriptions';
import Members from '../../pages/members';
import Sessions from '../../pages/sessions/router';

import EventForm from '../../pages/events/config/eventForm';
import Integration from '../../pages/ticket_option/integration/integration';
import useMixpanel from '../../context/useMixpanel';
import Discounts from '../../pages/discounts/discounts';
import EmailExport from '../../pages/email_export';
import { GoogleMapsContextProvider } from '../../context/googleMapsContext';
import TicketOptions from '../../pages/ticket_option/ticketOption';
import SellOnSite from '../../pages/sale_option/sell_on_site/sellOnSite';
import QRCodeShare from '../../pages/sale_option/qr_code/qrCodeShare';
import Taxs from '../../pages/organization/taxes';
import PageError from '../../pages/error/pageError';
import AuthRouteEvent from '../../utils/authRouteEvent';
import Imports from '../../pages/attendees/import/Imports';
import WaitingListEventRouter from './waitingListEventRouter';
import ResaleConfigRouter from './resaleConfigRouter';

const uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

function InventoryRedirect({ organization, event, baseRoute }) {
    const [{ inventory }] = useSeatedContext();

    // Inventory will be loaded thanks to InventoryManager
    if (!inventory) {
        return 'Loading inventory...';
    }

    return <Redirect to={`/ticket/O${organization.id}/E${event.id}/seating/${baseRoute}/inventory/${inventory.uuid}`} />;
}

InventoryRedirect.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    baseRoute: PropTypes.string.isRequired
};

function AttendeesRouter({ event, organization, match }) {
    return (
        <Suspense fallback={<Loading />}>
            <Attendees event={event} organization={organization} match={match} />
        </Suspense>
    );
}

AttendeesRouter.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};

function SeatedRoutes({ event, organization, path }) {
    const [{ inventory, inventories }, { setInventory }] = useSeatedContext();

    const _setInventory = React.useCallback(
        id => {
            if (inventory?.uuid === id) {
                return true;
            }

            let newInventory = inventories.find(({ uuid }) => uuid === id);
            if (!newInventory) {
                // Maybe changed events?
                setInventory(inventories[0]);
                return false;
            }
            setInventory(newInventory);
            return true;
        },
        [inventory, setInventory, inventories]
    );

    if (!inventories) {
        return <Loading />;
    }

    return (
        <Switch>
            {/* Availability */}
            <AuthRouteEvent exact path={`${path}/availability`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY']}>
                <InventoryRedirect organization={organization} event={event} baseRoute="availability" />
            </AuthRouteEvent>
            <AuthRouteEvent
                exact
                path={`${path}/availability/inventory/:inventoryId(${uuidRegex})`}
                allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY']}
                render={routeProps => <Redirect to={`/ticket/O${organization.id}/E${event.id}/seating/availability/inventory/${routeProps.match.params.inventoryId}/zone/root`} />}
            />
            <AuthRouteEvent
                exact
                path={`${path}/availability/inventory/:inventoryId(${uuidRegex})/zone/:zoneId(root|${uuidRegex})`}
                allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY']}
                render={({
                    history,
                    match: {
                        params: { inventoryId, zoneId }
                    }
                }) => {
                    let go_inventory = _setInventory(inventoryId);
                    if (!go_inventory) {
                        return <Redirect to={`/ticket/O${organization.id}/E${event.id}/seating/availability`} />;
                    }
                    return <Availability history={history} inventoryId={inventoryId} zoneId={zoneId} event={event} organization={organization} />;
                }}
            />

            {/* Quotas */}
            <AuthRouteEvent exact path={`${path}/quotas`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_QUOTA']}>
                <InventoryRedirect organization={organization} event={event} baseRoute="quotas" />
            </AuthRouteEvent>
            <AuthRouteEvent
                path={`${path}/quotas/inventory/:inventoryId(${uuidRegex})`}
                allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_QUOTA']}
                render={({ history, match }) => {
                    let go_inventory = _setInventory(match.params.inventoryId);
                    if (!go_inventory) {
                        return <Redirect to={`/ticket/O${organization.id}/E${event.id}/seating/quotas`} />;
                    }
                    return <Quotas inventoryId={match.params.inventoryId} history={history} match={match} />;
                }}
            />

            {/* Quotas DEV*/}
            <AuthRouteEvent
                path={`${path}/quotas_dev`}
                allow={['PyvarCapacity.QUOTAS']}
                render={({ history, match }) => {
                    return <QuotasDev organization={organization} event={event} history={history} match={match} />;
                }}
            />

            {/* Old manager attendees but it's still functional for map view */}
            <AuthRouteEvent
                path={`${path}/attendees/manage`}
                allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE']}
                render={({ match }) => {
                    if (!inventory) {
                        setInventory(inventories[0]);
                    }
                    return <AttendeesRouter match={match} event={event} organization={organization} />;
                }}
            />
            <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/E${event.id}/dashboard`} />} />
        </Switch>
    );
}

SeatedRoutes.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    path: PropTypes.string.isRequired
};

export default function EventRouter({ eventId, path, url, history }) {
    const menhirUrl = getMenhirUrl();
    const { t } = useTranslation();
    const [context, setContext] = useAppContext();
    const [loading, setLoading] = React.useState(true);
    const [eventCodeError, setEventCodeError] = React.useState(null);
    const toast = React.useContext(ToastContext);
    const mixpanel = useMixpanel();

    const loadEvent = React.useCallback(
        (cache = true) => {
            setLoading(true);
            return EventModel.get({
                id: eventId,
                organizationId: context.organization.id,
                query: {
                    sync: 'True'
                },
                cache: cache,
                allow_cache: cache
            })
                .then(event => {
                    setContext({
                        event,
                        event_timezone: event.timezone || 'Europe/Paris',
                        reloadEvent: loadEvent,
                        feature_flags: context.organization.feature_flags.concat(event.feature_flags)
                    });

                    Sentry.configureScope(scope => {
                        scope.setExtra('event_id', eventId);
                    });

                    mixpanel.register({
                        'Event ID': eventId
                    });
                })
                .catch(e => {
                    setEventCodeError(e.code);
                    console.error(e);
                    toast.error(t('ticket.event.retrievalError'));
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [eventId, context.organization, mixpanel]
    );

    React.useEffect(() => {
        if (context.event && context.event.id === eventId) {
            return;
        }

        loadEvent();
    }, [loadEvent]);

    if (loading) {
        // Loading new event
        return <Loading />;
    }

    if (!context.event) {
        return (
            <Template organization={context.organization}>
                <PageError errorCode={eventCodeError} backHomeUrl={`/ticket/O${context.organization?.id}`} />
            </Template>
        );
    }

    const { event, organization } = context;

    return (
        <SeatedContextProvider>
            <Template organization={organization} event={event} history={history}>
                <Switch>
                    {/* Event dashboard redirection */}
                    <AuthRouteEvent
                        exact
                        path={`${path}/`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}
                        render={() => {
                            let redirect = `${url}/dashboard`;
                            if (url[url.length - 1] === '/') {
                                redirect = `${url}dashboard`;
                            }
                            return <Redirect to={redirect} />;
                        }}
                    />
                    {/* home */}
                    <AuthRouteEvent exact path={`${path}/dashboard`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                        <EventDashboard organization={organization} event={event} prefixUrl={`/ticket/O${organization.id}/E${event.id}`} />
                    </AuthRouteEvent>

                    {/* attendees-import */}
                    <AuthRouteEvent exact path={`${path}/attendees/imports/new`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.IMPORT_ATTENDEES']}>
                        <ImportNew organization={organization} event={event} />
                    </AuthRouteEvent>

                    <AuthRouteEvent
                        exact
                        path={`${path}/attendees/import/:importID`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE']}
                        render={({
                            match: {
                                params: { importID }
                            }
                        }) => <Import importID={importID} organization={organization} event={event} />}
                    />
                    <AuthRouteEvent exact path={`${path}/attendees/import`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.IMPORT_ATTENDEES']}>
                        <Import organization={organization} event={event} />
                    </AuthRouteEvent>

                    <AuthRouteEvent exact path={`${path}/attendees/imports`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.IMPORT_ATTENDEES']}>
                        <Imports organization={organization} event={event} />
                    </AuthRouteEvent>

                    {/* Add attendee */}
                    <AuthRouteEvent
                        path={`${path}/attendees/add`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE']}
                        fallback={<AttendeesRedirect event={event} />}
                        render={({ match }) => <AddAttendee organization={organization} event={event} match={match} />}
                    />

                    {/* inviations light */}
                    <AuthRouteEvent
                        path={`${path}/communicate/invitations`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.INVITATION']}
                        fallback={<InvitationsRedirect event={event} />}
                        render={() => <InvitationsLight organization={organization} event={event} />}
                    />

                    {/* Manage members */}
                    <AuthRouteEvent
                        path={`${path}/attendees/members`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SUBSCRIPTIONS', 'PyvarCapacity.EVENT']}
                        render={() => <Members event={event} />}
                    />

                    {/* stock print */}
                    <AuthRouteEvent
                        path={`${path}/attendees/stock-print`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.PRINTER']}
                        fallback={<PrintStockRedirect event={event} />}
                        render={({ match }) => <AddAttendee organization={organization} event={event} match={match} isAddAttendees={false} />}
                    />

                    {/* Resale */}
                    <AuthRouteEvent
                        path={`${path}/resale`}
                        allow={['PyvarCapacity.INTERNAL_RESALE', 'PyvarCapacity.INTERNAL_RESALE_ADMIN']}
                        render={({ match: { path: resalePath } }) => <ResaleConfigRouter organization={organization} event={event} path={resalePath} />}
                    />

                    {/* Attendees statistics */}
                    <AuthRouteEvent path={`${path}/attendees/stats`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']} render={() => <AttendeesStats event={event} />} />

                    {/* Manager attendees/orders */}
                    <AuthRouteEvent
                        path={`${path}/manager`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ATTENDEE']}
                        render={({ match: { path } }) => <Manager organization={organization} event={event} path={path} />}
                    />

                    {/* Seating */}
                    <AuthRouteEvent
                        path={`${path}/seating`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SEATING_AVAILABILITY', 'PyvarCapacity.SEATING_QUOTA', 'PyvarCapacity.CONTINGENT', 'PyvarCapacity.ATTENDEE']}
                        render={({ match: { path } }) => (
                            <InventoryManager>
                                <SeatedRoutes organization={organization} event={event} path={path} />
                            </InventoryManager>
                        )}
                    />

                    {/* Rates */}
                    <AuthRouteEvent
                        path={`${path}/settings/rates`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.RATE', 'PyvarCapacity.RATE_TEMPLATE']}
                        render={({ match: { path } }) => <Rates organization={organization} event={event} path={path} menhirUrl={menhirUrl ? `${menhirUrl}/bo` : null} />}
                    />

                    {/* Tickets design */}
                    <AuthRouteEvent
                        isAdmin={false}
                        path={`${path}/settings/tickets_design/designs`}
                        allow={['PyvarCapacity.DESIGN_ADMIN', 'PyvarCapacity.DESIGN_TEMPLATES', 'PyvarCapacity.DESIGN']}
                        render={({ match: { path } }) => (
                            <TicketDesignEventRouter organization={context.organization} event={event} path={path} menhirUrl={menhirUrl ? `${menhirUrl}/bo` : null} />
                        )}
                    />

                    {/* Event detail */}
                    <AuthRouteEvent path={`${path}/settings/event`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT']}>
                        <GoogleMapsContextProvider>
                            <EventForm />
                        </GoogleMapsContextProvider>
                    </AuthRouteEvent>

                    {/* Integration */}
                    <AuthRouteEvent path={`${path}/settings/integration`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.ANALYTICS']}>
                        <Integration event={event} organization={organization} />
                    </AuthRouteEvent>

                    {/* Widget */}
                    <AuthRouteEvent
                        path={`${path}/channels/widget`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET']}
                        fallback={<Redirect path={path} to={`/ticket/O${organization.id}/E${event.id}/channels/widgetRedirect`} />}
                        render={({ match: { path } }) => <SellOnSite organization={organization} event={event} />}
                    />

                    {/* Queue widget */}
                    <AuthRouteEvent
                        exact
                        path={`${path}/settings/queue/:widgetKey`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET']}
                        render={({
                            match: {
                                params: { widgetKey }
                            }
                        }) => <Queue event={event} organization={organization} widgetKey={widgetKey} />}
                    />

                    {/* Taxes */}
                    <AuthRouteEvent
                        path={`${path}/taxes`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.TAX']}
                        render={routeProps => <Taxs organization={organization} event={event} />}
                    />

                    {/* Discounts */}
                    <AuthRouteEvent
                        path={`${path}/communicate/discounts`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.DISCOUNT']}
                        fallback={<Redirect to={`/ticket/O${organization.id}/E${event.id}/communicate/vouchers`} />}
                        render={({ match: { path } }) => <Discounts organization={organization} event={event} path={path} />}
                    />

                    {/* E-mail export */}
                    <AuthRouteEvent
                        path={`${path}/communicate/emails`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.EMAILS_EXPORT']}
                        render={() => <EmailExport event={event} />}
                    />

                    {/* QR Code */}
                    <AuthRouteEvent
                        path={`${path}/communicate/qr-code`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.WIDGET', 'PyvarCapacity.WIDGET_ADMIN']}
                        render={({ match: { path } }) => <QRCodeShare organization={organization} event={event} />}
                    />

                    {/* TicketOptions */}
                    <AuthRouteEvent
                        path={`${path}/sale`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SALE_OPTIONS', 'PyvarCapacity.ANALYTICS']}
                        render={({ match: { path } }) => <TicketOptions organization={organization} event={event} path={path} />}
                    />

                    {/* Minisite */}
                    <AuthRouteEvent path={`${path}/channels/page`} allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.MINISITE']}>
                        <MinisiteManager organization={organization} event={event} autoCreate={false} />
                    </AuthRouteEvent>

                    {/* Ticket Office Sell */}
                    <AuthRouteEvent
                        path={`${path}/ticket_offices`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.TICKET_OFFICE']}
                        render={() => <Redirect to={`/ticket/O${organization.id}/ticket_offices`} />}
                    />

                    {/* Forms */}
                    <AuthRouteEvent
                        path={`${path}/settings/forms`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.FORM']}
                        render={({ match: { path } }) => <Forms organization={organization} event={event} path={path} />}
                    />

                    {/* Subscriptions */}
                    <AuthRouteEvent
                        path={`${path}/settings/subscriptions`}
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SUBSCRIPTIONS']}
                        render={() => <Subscriptions event={event} />}
                    />

                    {/* Sessions (dates)*/}
                    <AuthRouteEvent
                        allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.SESSION']}
                        path={`${path}/settings/sessions`}
                        render={({ match: { path } }) => <Sessions organization={organization} event={event} path={path} />}
                    />

                    {/* Waiting lists */}
                    <AuthRouteEvent
                        isAdmin={false}
                        path={`${path}/waiting_lists`}
                        allow={['PyvarCapacity.WAITING_LIST_ADMIN', 'PyvarCapacity.WAITING_LIST']}
                        render={({ match: { path } }) => <WaitingListEventRouter organization={context.organization} event={event} path={path} />}
                    />
                    {/* Weez legacy bo routes */}
                    {/* if this loop is in a component, it breaks the default of the router switch on the 404. */}
                    {/* It is therefore necessary to leave this loop in the router directly. */}
                    {routes.map((weezRoute, index) => {
                        const { path: innerPath, route } = weezRoute;
                        const legacyMenhirUrl = menhirUrl ? `${menhirUrl}/bo` : null;
                        return legacyMenhirUrl ? <WeezBoRoute key={index} path={path + innerPath} weezRoute={route(legacyMenhirUrl, eventId)} /> : null;
                    })}

                    {/* Error */}
                    <Route render={() => <PageError errorCode={404} backHomeUrl={`/ticket/O${organization.id}/E${event.id}/dashboard`} />} />
                </Switch>
            </Template>
        </SeatedContextProvider>
    );
}

EventRouter.propTypes = {
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    path: PropTypes.string.isRequired,
    history: PropTypes.object
};
